// One item on one screen (round 4 frames f3b and f5b; round 5 f1a for a door): the panel that takes the tree's place.
// Back to list with the item's neighbours on its wall; the header (number, name, Open in Library for the owner, the
// catalog item and its version, verified or not, the recipe, "stands in for", Priced with, the money line); then the
// two sections that always show, Changes from standard (Approve, or "Approved 10-22 · Spencer · Undo") and Found by
// the job check (Fix / Keep); on the walk the Build by build strip; then, folded, Inputs, Calculations, Job standards
// it reads, Makes, Route, BOM and the item's own details and editing controls; the bar at the bottom: on Items built
// what an edit does, on the walk the check button that names what it approves.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { checkWords, itemDeviations, itemFindings, resolveJob, type Deviation, type Finding, type FlagRow, type ItemFinding, type Resolution, type ResolvedItem } from '@zerogap/engine';
import { deleteRow, insertRow, refresh, rpc, toast, updateRow, type Row } from '../../../lib/api';
import { nextNumber, prefixFor } from '../../../lib/create';
import { hours, money, qty } from '../../../lib/format';
import { baseBuild, ensureStored } from '../../../lib/job';
import { useSession } from '../../../lib/session';
import { Chain, Edit, Info, Ln, Money } from '../../../ui/kit';
import { bomPackWords } from '../../../ui/pack';
import { AiChangedTag, UnverifiedTag, verifyAddress } from '../../../ui/unverified';
import { CatalogPick, CustomLines, InputLine, MaterialPick, OperationPick, RecipePick, ValueEditor, showValue, writeItem } from '../ItemTab';
import { removeItem, type JobCtx } from '../Workspace';
import { addOnShort, itemShare, unitWords, wallShort, type WalkModel } from './model';
import { sizedName } from './Tree';
import { ClosingNote, FlagForm, FlagNote, FlagState, mmdd, raiseFlag, undoFlag } from './Flags';

export { mmdd };

export interface PanelProps {
  ctx: JobCtx;
  model: WalkModel;
  mode: 'items' | 'quick' | 'walk';
  item: ResolvedItem;
  findings: Finding[];
  /** The job's flags (the Quick check pack, 2026-10-24) and the seats, for who a flag waits on. */
  flags: FlagRow[];
  seats: Row[];
  users: Row[];
  buildPricing: (build: Row, addOnId: string | null) => Resolution | null;
  onPick: (id: string) => void;
  onBack: () => void;
  /** After a check: open the next stop. */
  onChecked: (id: string) => void;
  /** The Views pack (2026-10-24): the job's Views, the items placed on none, and "Place on…" (the third door into placing). */
  views?: Row[];
  unplaced?: ResolvedItem[];
  onPlace?: (viewId: string) => void;
}

/** The tab a jump came from, in words: a step's name, or a tool's label. */
export function tabLabel(ctx: JobCtx, key: string | null, tools: { key: string; label: string }[]): string {
  if (!key) return '';
  return ctx.steps.find((s) => s.tab === key)?.name ?? tools.find((t) => t.key === key)?.label ?? key;
}

export function JumpBack({ ctx }: { ctx: JobCtx }) {
  const s = useSession();
  const tools = s.setting<{ key: string; label: string }[]>('job_view_tools', []);
  if (!ctx.back) return null;
  return (
    <div className="back jumpback">
      <button type="button" className="btn read" onClick={() => ctx.go({ tab: ctx.back, lit: null, back: null })}>
        ‹ Back to {tabLabel(ctx, ctx.back, tools)}
      </button>
    </div>
  );
}

function FoldRow({ name, count, open, onToggle }: { name: string; count?: React.ReactNode; open: boolean; onToggle: () => void }) {
  return (
    <div
      className="fold"
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <span className={`chev${open ? ' open' : ''}`} />
      <b>{name}</b>
      {count !== undefined && <span className="ct">{count}</span>}
    </div>
  );
}

/** One change from standard: the value and the standard beside it; Approve, or who approved it and Undo. */
export function ChangeRow({ d, onApprove, onUndo, users, canEdit, note }: { d: Deviation; onApprove: () => void; onUndo: () => void; users: Row[]; canEdit: boolean; note?: React.ReactNode }) {
  const who = d.approved ? users.find((u) => u.id === d.approved!.by)?.name ?? 'Spencer' : '';
  return (
    <div className={`drow${d.approved ? ' ok' : ''}`}>
      <div className="v">
        {d.label} <b className="held">{d.value}</b>
        <small>
          {d.standard}
          {note}
        </small>
      </div>
      <div className="mini">
        {d.approved ? (
          <>
            <span className="st">
              Approved {mmdd(d.approved.at)} · {who}
            </span>
            {canEdit && (
              <button type="button" className="btn" onClick={onUndo}>
                Undo
              </button>
            )}
          </>
        ) : (
          canEdit && (
            <button type="button" className="btn pri" onClick={onApprove}>
              Approve
            </button>
          )
        )}
      </div>
    </div>
  );
}

/**
 * One finding of the job check, or a flag written by hand (rule `flag`): "Needs a fix" raises a flag to the engineer with
 * the finding's text and an optional note; "Fine as is" keeps it. The answered state reads as a chip with who and when, and
 * Undo; a flag the engineer closed reads "fixed · who · when" with his note until the check clears it (Q2 to Q4).
 */
export function FindingRow({ f, onAnswer, users, seats, canEdit, on }: { f: ItemFinding; onAnswer: (a: 'fix' | 'keep' | null) => void; users: Row[]; seats: Row[]; canEdit: boolean; on?: string }) {
  const who = f.answered ? users.find((u) => u.id === f.answered!.by)?.name ?? 'Spencer' : '';
  const isFlag = f.finding.rule === 'flag';
  const flag = f.flag;
  return (
    <div className={`drow fx${f.answered && !(flag && !flag.resolved_at) ? ' answered' : ''}${flag?.resolved_at ? ' dn' : ''}`}>
      <div className="v">
        {on && <b className="mono">{on} · </b>}
        {isFlag ? (
          <>
            Flag <b>{f.finding.what}</b>
            {flag && <small>{userName(users, flag.created_by)} · {mmdd(flag.created_at)}</small>}
          </>
        ) : (
          f.finding.what
        )}
        {(f.finding.builds?.length ?? 0) > 0 && <small>under {f.finding.builds!.join(', ')}</small>}
        {flag && <FlagNote flag={flag} canEdit={canEdit} />}
        {flag && <ClosingNote flag={flag} users={users} />}
      </div>
      <div className="mini">
        {flag ? (
          <>
            <FlagState flag={flag} users={users} seats={seats} onFinding={!isFlag} />
            {canEdit && !flag.resolved_at && (
              <button type="button" className="btn" onClick={() => onAnswer(null)}>
                Undo
              </button>
            )}
          </>
        ) : f.answered ? (
          <>
            <span className={`tag ${f.answered.answer === 'fix' ? 'fix' : 'ok'}`}>{f.answered.answer === 'fix' ? 'Needs a fix' : 'Fine as is'}</span>
            <span className="st">
              {who} · {mmdd(f.answered.at)}
            </span>
            {canEdit && (
              <button type="button" className="btn" onClick={() => onAnswer(null)}>
                Undo
              </button>
            )}
          </>
        ) : (
          canEdit && (
            <>
              <button type="button" className="btn" title="Raise a flag to the engineer with these words; a note is optional" onClick={() => onAnswer('fix')}>
                Needs a fix
              </button>
              <button type="button" className="btn" onClick={() => onAnswer('keep')}>
                Fine as is
              </button>
            </>
          )
        )}
      </div>
    </div>
  );
}

const userName = (users: Row[], id: string | null | undefined) => String(users.find((u) => u.id === id)?.name ?? 'Spencer');

export async function approveChange(ctx: JobCtx, all: ResolvedItem[], item: ResolvedItem, key: string, value: unknown) {
  const id = await ensureStored(item, ctx.jobId, all);
  await rpc('approve_change', { p_item: id, p_key: key, p_value: value ?? null });
  refresh('job_item');
}
export async function unapproveChange(item: ResolvedItem, key: string) {
  await rpc('unapprove_change', { p_item: item.id, p_key: key });
  refresh('job_item');
}

export function ItemPanel(p: PanelProps) {
  const { ctx, model, mode, item } = p;
  const s = useSession();
  const lib = ctx.r.lib!;
  const res = model.resOf(item.id);
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  const walk = mode !== 'items';
  const row = model.rowOf(item.id);
  const cat = item.catalog_item_id ? lib.catalog[item.catalog_item_id] : null;
  const recipe = cat?.recipes.find((x) => x.id === item.recipe_id);
  const parent = item.parent_item_id ? res.items.find((i) => i.id === item.parent_item_id) ?? model.res.items.find((i) => i.id === item.parent_item_id) ?? null : null;
  const kids = res.items.filter((i) => i.parent_item_id === item.id).sort((a, b) => (a.child_index ?? 0) - (b.child_index ?? 0));
  const stop = model.stopOf(item.id);
  const wall = model.walls.find((w) => w.stops.includes(stop!));
  const top = stop?.item ?? item;
  const share = itemShare(res, item);
  const devs = itemDeviations(item, res, lib, row as any);
  const finds = itemFindings(item, p.findings, row as any, p.flags);
  const notIn = model.notIn(item);
  // "+ Flag" in the Found-by-the-check header (Q5): the form opens in place
  const [flagging, setFlagging] = useState(false);
  const buildName = String(ctx.build?.name ?? 'the job as entered');
  const addOn = item.add_on_id ? ctx.data.addOns.find((a) => a.id === item.add_on_id) ?? null : null;
  const replaces = item.replaces_item_id ? model.res.items.find((i) => i.id === item.replaces_item_id)?.number ?? '' : '';
  const ref = useRef<HTMLDivElement>(null);

  // every section opens folded but Changes and Found (Spencer, 2026-10-22); a jump opens the one it lands in
  const [folds, setFolds] = useState<Record<string, boolean>>({});
  const lit = ctx.lit && ctx.itemId === item.id ? ctx.lit : null;
  useEffect(() => {
    setFolds(lit ? { route: lit.kind === 'step', bom: lit.kind === 'line', inputs: lit.kind === 'input' } : {});
  }, [item.id, lit?.kind, lit?.key]);
  useEffect(() => {
    if (!lit) return;
    // the panel settles as the job check and the pictures load; land on the row again until it has
    const go = () => ref.current?.querySelector('.lit')?.scrollIntoView({ block: 'center' });
    const ts = [80, 500, 1200, 2500].map((ms) => window.setTimeout(go, ms));
    return () => ts.forEach((t) => window.clearTimeout(t));
  }, [lit?.kind, lit?.key, folds.route, folds.bom, folds.inputs]);
  const fold = (k: string) => !!folds[k];
  const flip = (k: string) => setFolds({ ...folds, [k]: !folds[k] });

  const patch = async (x: Row) => {
    const id = await ensureStored(item, ctx.jobId, res.items);
    await updateRow('job_item', id, x);
  };

  // ---- the walk's answers ----
  const partDevs = kids.flatMap((k) => itemDeviations(k, res, lib, model.rowOf(k.id) as any).map((d) => ({ d, k })));
  // a finding that lives on two parts shows once, with both parts named, and one answer covers them (F3, 2026-10-24)
  const partFinds = (() => {
    const out: { f: ItemFinding; ks: ResolvedItem[] }[] = [];
    for (const k of kids) {
      for (const f of itemFindings(k, p.findings, model.rowOf(k.id) as any, p.flags)) {
        const have = out.find((x) => x.f.key === f.key);
        if (have) {
          have.ks.push(k);
          if (!have.f.answered && f.answered) have.f = f;
        } else out.push({ f, ks: [k] });
      }
    }
    return out;
  })();
  const words = checkWords(item, kids.length, [...devs, ...partDevs.map((x) => x.d)], [...finds, ...partFinds.map((x) => x.f)]);
  const state = stop && !parent ? model.stateOf(stop) : 'never';
  // Needs a fix raises a flag to the engineer with the finding's words (and answers the finding); Fine as is keeps it; Undo
  // takes the flag back while it waits, or the answer (2026-10-24)
  const answer = async (f: ItemFinding, a: 'fix' | 'keep' | null, on: ResolvedItem) => {
    if (a === null && f.flag && !f.flag.resolved_at) {
      await undoFlag(f.flag.id);
      return;
    }
    if (a === null && f.finding.rule === 'flag') return;
    const id = await ensureStored(on, ctx.jobId, res.items);
    if (a === 'fix') {
      await raiseFlag(ctx, { target: 'item', item: id, text: f.finding.what, findingKey: f.key });
      return;
    }
    await rpc('answer_finding', { p_item: id, p_key: f.key, p_answer: a });
    refresh('job_item');
  };
  const flagItem = async (text: string, note: string) => {
    const id = await ensureStored(item, ctx.jobId, res.items);
    await raiseFlag(ctx, { target: 'item', item: id, text, note });
    setFlagging(false);
    toast(`Flag on ${item.number} raised.`);
  };
  const check = async () => {
    for (const d of devs) if (!d.approved) await rpc('approve_change', { p_item: item.id, p_key: d.key, p_value: d.raw ?? null });
    for (const { d, k } of partDevs) if (!d.approved) await approveChange(ctx, res.items, k, d.key, d.raw);
    await rpc('check_item', { p_id: item.id, p_version: res.standards_version_id });
    refresh('job_item');
    toast(`${item.number} checked.`);
    p.onChecked(item.id);
  };
  const uncheck = async () => {
    await rpc('uncheck_item', { p_id: item.id });
    refresh('job_item');
  };

  // ---- the old item tab's editing controls ----
  // Adjust this item's route / BOM opens the rows for editing and writes nothing (fix 3, 2026-10-26: a click with no edit
  // emptied S1A); a value typed on a recipe row lands as the item's own line beside its recipe, never as a whole-item conversion
  const [adjusting, setAdjusting] = useState(false);
  useEffect(() => setAdjusting(false), [item.id]);
  const makeCustom = async () => {
    if (!ctx.r.jobIn || !row) return;
    if (!window.confirm(`Make ${item.number} custom? It leaves its catalog item and recipe and carries this route and BOM as its own; the price may change. Back to the recipe undoes it.`)) return;
    const alone = resolveJob(lib, { ...ctx.r.jobIn, items: ctx.r.jobIn.items.filter((i) => i.id === item.id), subLines: [], decisionLines: [] }, item.add_on_id ? { addOns: [item.add_on_id] } : {});
    const g = alone.groups.find((x) => x.item_ids.includes(item.id));
    if (!g) return;
    await updateRow('job_item', item.id, {
      is_custom: true,
      custom_route: g.steps.filter((st) => Number(st.qty) > 0).map((st) => ({ operation_id: st.operation_id, qty: st.qty, note: st.note })),
      custom_bom: g.bom.filter((b) => Number(b.qty) > 0 && b.material_id).map((b) => ({ material_id: b.material_id, qty: b.qty, note: b.note })),
    });
    toast('This item now carries its own route and BOM. It is flagged as custom and its Checked mark is cleared.');
  };
  const changeCatalogItem = async (id: string) => {
    if (!row || (id === item.catalog_item_id && !item.is_custom)) return;
    const to = lib.catalog[id];
    if (!to) return;
    const made = ctx.data.items.filter((k) => k.parent_item_id === item.id && k.child_index !== null);
    const ask = item.is_custom && !cat
      ? `Make ${item.number} a ${to.name}? Its number, name, location and notes stay; the inputs start at the ${to.name}'s defaults, and its route and materials come from the recipe from then on.`
      : `Make ${item.number} a ${to.name}? Width, height, depth and every input both items share carry over; a recipe picked by hand is dropped${made.length ? `, and the ${made.length} ${made.length === 1 ? 'child it made' : 'children it made'} as a ${cat?.name ?? 'catalog item'} go with it` : ''}.`;
    if (!window.confirm(ask)) return;
    for (const k of made) await deleteRow('job_item', k.id);
    await updateRow('job_item', item.id, { catalog_item_id: id, recipe_id: null, is_custom: false, custom_route: [], custom_bom: [] });
    toast(`${item.number} is a ${to.name} now.`);
  };
  const duplicateItem = async () => {
    if (!row) return;
    const prefix = /^([A-Za-z]+)-/.exec(item.number)?.[1] ?? (cat ? prefixFor(cat.name) : 'X');
    const number = nextNumber(ctx.data.items.map((i) => String(i.number ?? '')), prefix);
    const made = await insertRow('job_item', {
      job_id: ctx.jobId, number, name: item.name, catalog_item_id: row.catalog_item_id ?? null, recipe_id: row.recipe_id ?? null,
      inputs: row.inputs ?? {}, overrides: row.overrides ?? {}, is_custom: !!row.is_custom, custom_route: row.custom_route ?? [], custom_bom: row.custom_bom ?? [],
      note: row.note ?? null, location: row.location ?? null, room_id: row.room_id ?? null, add_on_id: row.add_on_id ?? null, sort: Math.max(0, ...ctx.data.items.map((i) => Number(i.sort ?? 0))) + 1,
    });
    p.onPick(made.id as string);
    toast(`${number} is a copy of ${item.number}. Change what is different; the doors or drawer boxes it makes start fresh.`);
  };

  const description = item.inputs.find((i) => i.key === 'description');
  const inputs = item.inputs.filter((i) => i.key !== 'description' && !i.hidden);
  const heldInputs = inputs.filter((i) => i.source === 'override').length;
  const heldStd = item.standards_used.filter((st) => st.overridden).length;

  // the item's neighbours on its wall, for the back bar
  const onWall = wall ? wall.stops.filter((x) => x.item) : [];
  const at = onWall.findIndex((x) => x.item?.id === top.id);
  const prev = at > 0 ? onWall[at - 1]!.item! : null;
  const next = at >= 0 && at < onWall.length - 1 ? onWall[at + 1]!.item! : null;
  const wallName = wall ? (wall.view ? wallShort(wall.name) : 'Whole room') : '';

  // a child reads the reveals its box holds (the box's door formulas read them): one approval on the box, shown here too
  const parentRow = parent ? model.rowOf(parent.id) : undefined;
  const parentReveals = parent ? itemDeviations(parent, model.resOf(parent.id), lib, parentRow as any).filter((d) => d.key.startsWith('std:reveal')) : [];

  // ---- the build by build strip ----
  const builds = ctx.data.builds;
  const strip = walk && !parent && builds.length > 1 ? buildStrip(p, item) : null;

  return (
    <div className="walkpanel" ref={ref}>
      {lit && <JumpBack ctx={ctx} />}
      <div className="back">
        <button type="button" className="btn read" onClick={p.onBack}>
          ‹ Back to list
        </button>
        {parent ? (
          <span className="where">
            <button type="button" className="linklike" onClick={() => p.onPick(parent.id)}>
              {parent.number}
            </button>{' '}
            › {item.number}
            {wallName ? ` · ${wallName}` : ''}
            {ctx.showFronts ? ' · Fronts' : ''}
          </span>
        ) : (
          <span className="where">
            {prev && (
              <button type="button" className="linklike" onClick={() => p.onPick(prev.id)}>
                ‹ {prev.number}
              </button>
            )}
            <span>
              {at + 1} of {onWall.length} on {wallName}
            </span>
            {next && (
              <button type="button" className="linklike" onClick={() => p.onPick(next.id)}>
                {next.number} ›
              </button>
            )}
          </span>
        )}
      </div>

      <div className="ph">
        <div className="t">
          <span className="no">{item.number}</span>
          <b>{parent ? sizedName(item) : item.name || item.catalog_item_name}</b>
          <span className="sp" />
          {parent ? (
            <span className="chip">made by {parent.number}</span>
          ) : walk ? (
            notIn ? <span className="tag off">not in {buildName}</span> : <span className="chip">under {buildName}</span>
          ) : (
            cat &&
            s.can('library.open_from_job') && (
              <>
                <Link className="lnk" to={`/library/catalog/${cat.id}`} target="_blank" rel="noopener" title="Opens the catalog item in the Library in a new tab, where its recipe is edited (an edit there unverifies it).">
                  Open in Library
                </Link>
                <span className="tag own">Owner only</span>
              </>
            )
          )}
        </div>
        <div className="sub">
          {parent ? (
            <>
              {devs.length > 0 ? <span className="tag chg">{devs.length} held</span> : item.unverified.any ? <UnverifiedTag items={[item]} /> : <span className="tag ok">verified</span>}
              <span>·</span>
              <span>
                {item.catalog_item_name ?? 'custom'}
                {item.recipe_name ? ` · recipe ${item.recipe_name}` : ''}
              </span>
              <span>·</span>
              <span>size from {parent.number}'s calculations</span>
            </>
          ) : (
            <>
              <span>{item.is_custom && !cat ? 'custom item' : `${cat?.name ?? item.catalog_item_name ?? ''}${cat?.version ? ` · v${cat.version}` : ''}`}</span>
              {/* F15 (2026-10-24): when only the library is unverified, the one hatched tag reads "unverified · recipe"; an AI's own edit on the row has its own tag beside it */}
              {item.is_custom ? <span className="tag unv">unverified</span> : item.unverified.any ? <UnverifiedTag items={[item]}>{row?.unverified_since ? 'unverified' : 'unverified · recipe'}</UnverifiedTag> : !walk && <span className="tag ok">verified</span>}
              <span>·</span>
              <span>recipe {item.is_custom ? 'typed by hand' : item.recipe_name ?? '—'}</span>
              {walk && (
                <>
                  <span>·</span>
                  <span>priced with {addOn ? `${addOnShort(String(addOn.name))} add-on` : 'base'}</span>
                </>
              )}
              {replaces && <span className="tag addon">stands in for {replaces}</span>}
              {!walk && canEdit && item.stored && p.onPlace && (p.views?.length ?? 0) > 0 && (p.unplaced ?? []).some((u) => u.id === item.id) && (
                <>
                  <span>·</span>
                  <select className="placeon" value="" aria-label="Place this item on a View" title="This item has a mark on no View: pick the sheet it sits on, then click where" onChange={(e) => e.target.value && p.onPlace!(e.target.value)}>
                    <option value="">Place on…</option>
                    {p.views!.map((v, k) => (
                      <option key={v.id} value={v.id}>
                        {v.name || v.kind || `View ${k + 1}`}
                      </option>
                    ))}
                  </select>
                </>
              )}
              {row?.unverified_since && <AiChangedTag table="job_item" row={row as any} users={p.users as any[]} />}
              {walk && cat && s.can('library.open_from_job') && (
                <Link className="lnk" to={`/library/catalog/${cat.id}`} target="_blank" rel="noopener">
                  Open in Library
                </Link>
              )}
            </>
          )}
        </div>
        {!walk && !parent && item.stored && ctx.data.addOns.length > 0 && (
          <div className="pw">
            <span className="lbl">Priced with</span>
            <select disabled={!canEdit} value={(row?.add_on_id as string | null) ?? ''} onChange={(e) => void patch(e.target.value ? { add_on_id: e.target.value } : { add_on_id: null, replaces_item_id: null })}>
              <option value="">Base</option>
              {ctx.data.addOns.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · add-on
                </option>
              ))}
            </select>
            <span className="only">base, or one of the add-ons from the Scope table; shows the same on Scope</span>
          </div>
        )}
        <div className="money">
          <b>
            <Money cents={item.price_cents} />
          </b>
          {parent ? (
            <span>priced inside {parent.number}</span>
          ) : (
            kids.length > 0 && (
              <span>
                <Money cents={item.rollup_price_cents} /> with its parts
              </span>
            )
          )}
          {Number(item.labor_minutes) > 0 && <span>{hours(kids.length ? item.rollup_labor_minutes : item.labor_minutes)}</span>}
        </div>
      </div>

      {notIn && walk && (
        <div className="sect">
          <p className="only">The {buildName} build skips this item and its price. It is still walked once, under the builds that carry it.</p>
        </div>
      )}

      {/* changes from standard: always open */}
      {devs.length + parentReveals.length + partDevs.length > 0 ? (
        <div className="sect">
          <h4>Changes from standard · {devs.length + parentReveals.length + partDevs.length}</h4>
          {devs.map((d) => (
            <ChangeRow key={d.key} d={d} users={p.users} canEdit={canEdit} onApprove={() => void approveChange(ctx, res.items, item, d.key, d.raw)} onUndo={() => void unapproveChange(item, d.key)} />
          ))}
          {partDevs.length > 0 && <p className="only">On its parts, approved with {item.number}:</p>}
          {partDevs.map(({ d, k }) => (
            <ChangeRow key={`${k.id}-${d.key}`} d={d} users={p.users} canEdit={canEdit} note={<> · on {k.number}</>} onApprove={() => void approveChange(ctx, res.items, k, d.key, d.raw)} onUndo={() => void unapproveChange(k, d.key)} />
          ))}
          {parent &&
            parentReveals.map((d) => (
              <ChangeRow
                key={`p-${d.key}`}
                d={d}
                users={p.users}
                canEdit={canEdit}
                note={<> · held on {parent.number}, read by this {/drawer/i.test(item.name) ? 'front' : 'door'}; one approval covers the box and its fronts</>}
                onApprove={() => void approveChange(ctx, model.resOf(parent.id).items, parent, d.key, d.raw)}
                onUndo={() => void unapproveChange(parent, d.key)}
              />
            ))}
        </div>
      ) : (
        finds.length + partFinds.length === 0 && (
          <div className="sect">
            <h4>Changes from standard · 0</h4>
            <p className="only">{parent ? `Follows ${parent.number}; nothing held on this part.` : 'Matches the job standards and its catalog item; nothing found by the check.'}</p>
          </div>
        )
      )}

      {/* found by the job check: always open; on the walk the header carries + Flag, a flag in Spencer's own words (Q3, Q5) */}
      {(finds.length + partFinds.length > 0 || (walk && canEdit)) && (
        <div className="sect">
          <h4>
            Found by the job check · {finds.length + partFinds.length}
            {walk && canEdit && !flagging && (
              <button type="button" className="btn flagbtn" title={`Flag something on ${item.number} for the engineer, in your own words`} onClick={() => setFlagging(true)}>
                + Flag
              </button>
            )}
          </h4>
          {flagging && <FlagForm what={`on ${item.number}`} onFlag={flagItem} onCancel={() => setFlagging(false)} />}
          {finds.map((f) => (
            <FindingRow key={f.key} f={f} users={p.users} seats={p.seats} canEdit={canEdit} onAnswer={(a) => void answer(f, a, item)} />
          ))}
          {partFinds.length > 0 && <p className="only">On its parts, checked with {item.number}:</p>}
          {partFinds.map(({ f, ks }) => (
            <FindingRow key={`${ks.map((k) => k.id).join('+')}-${f.key}`} f={f} users={p.users} seats={p.seats} canEdit={canEdit} on={ks.map((k) => k.number).join(', ')} onAnswer={(a) => void Promise.all(ks.map((k) => answer(f, a, k)))} />
          ))}
          {finds.length + partFinds.length === 0 && !flagging && <p className="only">Nothing found by the check. A flag here reaches the engineer and the check waits on it.</p>}
        </div>
      )}

      {strip}

      {parent && <SizeWorkedOut item={item} parent={parent} />}

      {/* ---- folded ---- */}
      {!item.is_custom && (
        <>
          <FoldRow name="Inputs" count={`${inputs.length}${heldInputs ? ` · ${heldInputs} held on this item` : ''}${parent ? ` · from ${parent.number}` : ''}`} open={fold('inputs')} onToggle={() => flip('inputs')} />
          {fold('inputs') && (
            <div className="sect inputs">
              {inputs.map((i) => (
                <InputLine key={i.key} ctx={ctx} item={item} input={i} canEdit={canEdit} all={res.items} lit={lit?.kind === 'input' && lit.key === i.key} />
              ))}
              {[...item.rule_warnings, ...item.warnings].map((w) => (
                <div key={w} className="warn">
                  {w}
                </div>
              ))}
              <p className="only">Locked follows Job setup or its parent; gold is held on this item and stays gold after you approve it; plain is typed for it.</p>
            </div>
          )}
        </>
      )}
      {item.is_custom && [...item.rule_warnings, ...item.warnings].length > 0 && (
        <div className="sect">
          {[...item.rule_warnings, ...item.warnings].map((w) => (
            <div key={w} className="warn">
              {w}
            </div>
          ))}
        </div>
      )}

      {!item.is_custom && item.derived.length > 0 && (
        <>
          <FoldRow name="Calculations" count={`${item.derived.length} · worked out from the inputs`} open={fold('calc')} onToggle={() => flip('calc')} />
          {fold('calc') && (
            <div className="sect">
              {item.derived.map((d) => (
                <Ln
                  key={d.key}
                  left={
                    <>
                      {d.label}
                      <Chain expr={d.expr} trace={d.trace} note="Worked out from the inputs. It changes when they do." />
                    </>
                  }
                  right={<span className="lk">{qty(d.value)}</span>}
                />
              ))}
            </div>
          )}
        </>
      )}

      {item.standards_used.length > 0 && (
        <>
          <FoldRow name="Job standards it reads" count={`${item.standards_used.length} · ${heldStd} held on this item`} open={fold('std')} onToggle={() => flip('std')} />
          {fold('std') && <StandardsRead ctx={ctx} item={item} all={res.items} canEdit={canEdit} />}
        </>
      )}

      {kids.length > 0 && (
        <>
          <FoldRow name="Makes" count={`${kids.length} ${kids.length === 1 ? 'part' : 'parts'} · ${kids.map((k) => k.number).join(', ')} · also in the tree`} open={fold('makes')} onToggle={() => flip('makes')} />
          {fold('makes') && (
            <div className="sect">
              {kids.map((k) => (
                <Ln
                  key={k.id}
                  onClick={() => p.onPick(k.id)}
                  left={
                    <>
                      <b className="mono">{k.number}</b> · {sizedName(k)}
                      {k.recipe_name && <small> · {k.recipe_name}</small>}
                    </>
                  }
                  right={
                    <span className="nowrap">
                      <Money cents={k.rollup_price_cents} /> · {hours(k.rollup_labor_minutes)}
                    </span>
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      {item.is_custom ? (
        <>
          <FoldRow name="Route and BOM" count="typed on this item" open={fold('custom')} onToggle={() => flip('custom')} />
          {fold('custom') && (
            <div className="sect custom">
              <CustomLines ctx={ctx} item={item} canEdit={canEdit} res={res} />
            </div>
          )}
        </>
      ) : (
        <>
          <FoldRow name="Route" count={`${share.steps.length} ${share.steps.length === 1 ? 'step' : 'steps'} · ${Math.round(share.minutes)} min${recipe ? ` · ${recipe.name}` : ''}${adjusting ? ' · adjusting' : ''}`} open={fold('route')} onToggle={() => flip('route')} />
          {fold('route') && <RouteRows ctx={ctx} item={item} res={res} row={row} canEdit={canEdit} litKey={lit?.kind === 'step' ? lit.key : null} adjusting={adjusting} onAdjust={() => setAdjusting((v) => !v)} patch={patch} />}
          <FoldRow name="BOM" count={`${share.bom.length} ${share.bom.length === 1 ? 'line' : 'lines'} · ${money(share.cents)}${adjusting ? ' · adjusting' : ''}`} open={fold('bom')} onToggle={() => flip('bom')} />
          {fold('bom') && <BomRows ctx={ctx} item={item} res={res} row={row} canEdit={canEdit} litKey={lit?.kind === 'line' ? lit.key : null} adjusting={adjusting} onAdjust={() => setAdjusting((v) => !v)} patch={patch} />}
        </>
      )}

      <FoldRow name="Item details" count="number, name, notes, recipe, catalog item, duplicate, remove" open={fold('edit')} onToggle={() => flip('edit')} />
      {fold('edit') && (
        <div className="sect">
          <Ln left="Item number" right={<Edit className="short right" disabled={!canEdit} value={item.number} onSave={(v) => patch({ number: v })} />} />
          <Ln left="Name" right={<Edit disabled={!canEdit} value={item.name} onSave={(v) => patch({ name: v })} />} />
          <Ln left="Location" right={<Edit disabled={!canEdit} value={row?.location ?? ''} onSave={(v) => patch({ location: v || null })} />} />
          {ctx.data.rooms.length > 0 && (
            <Ln
              left={
                <>
                  Room
                  <Info>The room this item is in. Its standards are the room's own where the room holds them (Job setup → Rooms), the job's otherwise. A door or drawer box the item creates is in the same room.</Info>
                </>
              }
              right={
                parent ? (
                  <span className="lk">{ctx.data.rooms.find((x) => x.id === item.room_id)?.name ?? 'no room'} · with its parent</span>
                ) : (
                  <select disabled={!canEdit} value={item.room_id ?? ''} onChange={(e) => void patch({ room_id: e.target.value || null })}>
                    <option value="">No room · the job's standards</option>
                    {ctx.data.rooms.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                        {x.own_standards ? ' · its own standards' : ''}
                      </option>
                    ))}
                  </select>
                )
              }
            />
          )}
          {walk && !parent && item.stored && ctx.data.addOns.length > 0 && (
            <Ln
              left="Priced with"
              right={
                <select disabled={!canEdit} value={(row?.add_on_id as string | null) ?? ''} onChange={(e) => void patch(e.target.value ? { add_on_id: e.target.value } : { add_on_id: null, replaces_item_id: null })}>
                  <option value="">Base</option>
                  {ctx.data.addOns.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} · add-on
                    </option>
                  ))}
                </select>
              }
            />
          )}
          {cat && cat.recipes.length > 1 && !item.is_custom && (
            <Ln
              left={
                <>
                  Recipe
                  <Info>Which of its recipes this item resolves through. "As the job says" follows {cat.recipe_standard ? `the job's ${lib.standards.find((d) => d.key === cat.recipe_standard)?.label ?? cat.recipe_standard}` : 'the default recipe'}; a pick here is this item's own.</Info>
                </>
              }
              right={
                <select disabled={!canEdit} value={row?.recipe_id ?? ''} onChange={(e) => void patch({ recipe_id: e.target.value || null })} style={{ maxWidth: 280 }}>
                  <option value="">As the job says{!row?.recipe_id && item.recipe_name ? ` · ${item.recipe_name}` : ''}</option>
                  {(item.recipes ?? []).map((r) => (
                    <option key={r.recipe_id} value={r.recipe_id} disabled={!!r.unavailable && r.recipe_id !== row?.recipe_id}>
                      {r.name}
                      {r.unavailable ? ` · not available (${r.unavailable})` : ''}
                    </option>
                  ))}
                </select>
              }
            />
          )}
          {description && <InputLine ctx={ctx} item={item} input={description} canEdit={canEdit} all={res.items} />}
          <Ln
            className="wide"
            left={
              <>
                Notes
                <Info>Anything special about this item the shop should see: it shows on the work order beside the item. The note is saved when you leave the box.</Info>
              </>
            }
            under={<Edit multiline disabled={!canEdit} value={item.note ?? ''} placeholder="nothing special" onSave={(v) => patch({ note: v || null })} className="note" />}
          />
          {canEdit && item.stored && !parent && (cat || item.is_custom) && (
            <Ln
              left={
                <>
                  Catalog item
                  <Info>Make this item another catalog item. Width, height, depth and every input both items share carry over; the rest start at the new item's defaults. The doors or drawer boxes it made as the old item go with it.</Info>
                </>
              }
              right={<CatalogPick lib={lib} value={item.is_custom ? '' : item.catalog_item_id ?? ''} onPick={(id) => void changeCatalogItem(id)} />}
            />
          )}
          {canEdit && item.stored && (
            <div className="row mid" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {!parent && (
                <button type="button" className="btn" title="A copy of this item on this job, with the same inputs, recipe and notes, under the next free number" onClick={() => void duplicateItem()}>
                  Duplicate
                </button>
              )}
              {item.is_custom && (
                <button
                  type="button"
                  className="btn"
                  onClick={async () => {
                    await insertRow('engineering_request', { job_item_id: item.id, title: `${item.name || item.number} (from ${ctx.data.opportunity?.name ?? 'a job'})`, requested_by: s.user?.id ?? null });
                    toast('Sent to the Engineering queue. This job keeps its own copy.');
                  }}
                >
                  Make this reusable
                </button>
              )}
              {item.is_custom && item.catalog_item_id && (
                <button type="button" className="btn" onClick={() => void updateRow('job_item', item.id, { is_custom: false, custom_route: [], custom_bom: [] })}>
                  Back to the recipe
                </button>
              )}
              {!item.is_custom && cat && !parent && (
                <button type="button" className="btn" title="The whole item becomes custom: it leaves its catalog item and recipe and carries this route and BOM as its own, typed on the item. Asks first; the price may change." onClick={() => void makeCustom()}>
                  Make this item custom
                </button>
              )}
              <button type="button" className="btn" onClick={() => void removeItem(ctx, item)}>
                Remove item
              </button>
            </div>
          )}
        </div>
      )}

      {/* the bar at the bottom */}
      <div className="checkbar">
        {!walk ? (
          <span className="nx">Edits save as you type and clear this item's Checked mark; the Engineering check lists it as moved.</span>
        ) : parent ? (
          <span className="nx">
            Parts are checked with {parent.number}: Check {parent.number} covers this one{parentReveals.length ? ' and its held reveal once approved' : ''}.
          </span>
        ) : state === 'checked' ? (
          <>
            {canEdit && (
              <button type="button" className="btn bigcheck done" onClick={() => void uncheck()}>
                Checked · Undo
              </button>
            )}
            <span className="nx">
              Checked {mmdd(row?.checked_at)} · {p.users.find((u) => u.id === row?.checked_by)?.name ?? 'Spencer'}. An edit to the item, its parts or its catalog item clears it.
            </span>
          </>
        ) : (
          <>
            {canEdit && (
              <button type="button" className="btn bigcheck" disabled={words.blocked > 0} onClick={() => void check()}>
                {words.blocked > 0 ? `Fix or keep ${words.blocked} before checking ${item.number}` : words.label}
              </button>
            )}
            <span className="nx">
              {words.blocked > 0 ? `Then: ${words.label}. ` : ''}
              {nextWords(model, top)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function nextWords(model: WalkModel, top: ResolvedItem): string {
  const stop = model.stopOf(top.id);
  const wall = model.walls.find((w) => w.stops.includes(stop!));
  const list = wall?.stops ?? [];
  const at = list.findIndex((x) => x === stop);
  const nx = list.slice(at + 1).find((x) => model.stateOf(x) !== 'checked');
  return nx ? `Next, ${nx.item?.number ?? (nx.kind === 'job_route' ? 'the job route' : 'the Job BOM')} opens; the list stays put.` : 'Then the next stop opens.';
}

/** The Build by build strip: the open item under every build, the open build's column lit, a cell that differs from the base's bold. */
function buildStrip(p: PanelProps, item: ResolvedItem) {
  const { ctx } = p;
  const base = baseBuild(ctx.data.builds) ?? ctx.data.builds[0]!;
  const cells = [...ctx.data.builds].sort((a, b) => Number(!!b.is_base) - Number(!!a.is_base)).map((b) => {
    const r = p.buildPricing(b, item.add_on_id ?? null);
    const it = r?.items.find((i) => i.id === item.id) ?? null;
    if (!r || !it) return { b, box: 'not in this build', route: '—', bom: '—', price: '—', carried: false };
    const cat = it.catalog_item_id ? ctx.r.lib!.catalog[it.catalog_item_id] : null;
    const missing = !!cat && cat.recipes.length > 0 && !it.recipe_id;
    if (missing) return { b, box: 'not in this build', route: '—', bom: '—', price: '—', carried: false };
    const sh = itemShare(r, it);
    return {
      b,
      box: it.is_custom ? 'typed by hand' : it.recipe_name ?? '—',
      route: `${sh.steps.length} ${sh.steps.length === 1 ? 'step' : 'steps'} · ${Math.round(sh.minutes)} min`,
      bom: `${sh.bom.length} ${sh.bom.length === 1 ? 'line' : 'lines'} · ${money(sh.cents)}`,
      price: money(it.rollup_price_cents),
      carried: true,
    };
  });
  const baseCell = cells.find((c) => c.b.id === base.id) ?? cells[0]!;
  const rows: { k: string; f: 'box' | 'route' | 'bom' | 'price' }[] = [
    { k: 'Box', f: 'box' },
    { k: 'Route', f: 'route' },
    { k: 'BOM', f: 'bom' },
    { k: 'Price', f: 'price' },
  ];
  const anyDiff = rows.some((r) => cells.some((c) => c[r.f] !== baseCell[r.f]));
  const openId = ctx.build?.id ?? base.id;
  return (
    <div className="bstrip">
      <h4>Build by build</h4>
      <table className="bt">
        <thead>
          <tr>
            <th />
            {cells.map((c) => (
              <th key={c.b.id} className={c.b.id === openId ? 'on' : ''}>
                {c.b.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.k}>
              <td className="k">{r.k}</td>
              {cells.map((c) => (
                <td key={c.b.id} className={`${c.b.id === openId ? 'on' : ''}${c[r.f] !== baseCell[r.f] ? ' diff' : ''}`}>
                  {c[r.f]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="only">{anyDiff ? 'Bold: what a build changes on the item against the base. One Checked mark covers every build.' : 'Every build carries it the same way. One Checked mark covers every build.'}</p>
    </div>
  );
}

/** A part's size as its box works it out: its width and height, and the box's calculations behind them. */
function SizeWorkedOut({ item, parent }: { item: ResolvedItem; parent: ResolvedItem }) {
  const w = item.inputs.find((i) => i.key === 'width' && !i.hidden);
  const h = item.inputs.find((i) => i.key === 'height' && !i.hidden);
  if (!w || !h || w.value === null || h.value === null) return null;
  const word = /drawer/i.test(`${item.name} ${item.catalog_item_name ?? ''}`) ? 'front' : 'door';
  const behind = parent.derived.filter((d) => /door|front|drawer/i.test(`${d.key} ${d.label}`));
  return (
    <div className="sect">
      <h4>Size, worked out</h4>
      <div className="calc">
        <div>
          <span className="k">Width</span>
          <span>
            <b>{showValue(w.value, 'number', 'in')}</b> {w.source === 'parent' ? `from ${parent.number}` : w.source === 'override' ? 'held on this part' : ''}
          </span>
        </div>
        <div>
          <span className="k">Height</span>
          <span>
            <b>{showValue(h.value, 'number', 'in')}</b> {h.source === 'parent' ? `from ${parent.number}` : h.source === 'override' ? 'held on this part' : ''}
          </span>
        </div>
        {behind.map((d) => (
          <div key={d.key}>
            <span className="k">{d.label}</span>
            <span>
              {qty(d.value)}
              <Chain expr={d.expr} trace={d.trace} note={`${parent.number} works out the ${word} sizes from its width and the reveals it reads.`} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** The job standards an item reads, each with the tick that holds a value on this item. */
function StandardsRead({ ctx, item, all, canEdit }: { ctx: JobCtx; item: ResolvedItem; all: ResolvedItem[]; canEdit: boolean }) {
  const lib = ctx.r.lib!;
  return (
    <div className="sect">
      <table className="lt st">
        <thead>
          <tr>
            <th>Standard</th>
            <th>Job</th>
            <th>This item</th>
          </tr>
        </thead>
        <tbody>
          {item.standards_used.map((st) => {
            const def = lib.standards.find((d) => d.key === st.key);
            if (!def) return null;
            const type = def.data_type;
            const unit = type === 'number' ? def.unit ?? null : null;
            const text = (v: unknown) =>
              type === 'material' ? lib.materials[String(v)]?.description ?? '—' : type === 'catalog_item' ? lib.catalog[String(v)]?.name ?? '—' : type === 'recipe' ? String(v ?? '—') : showValue(v as any, type, unit);
            const save = (v: unknown) => void writeItem(ctx, item, 'overrides', st.key, v as any, all);
            return (
              <tr key={st.key}>
                <td>
                  <label className="check">
                    <input type="checkbox" disabled={!canEdit} checked={st.overridden} title="Hold a value on this item" onChange={(e) => save(e.target.checked ? st.job_value : null)} />
                    {st.label}
                  </label>
                </td>
                <td className="s">{text(st.job_value)}</td>
                <td>
                  {!st.overridden ? (
                    <span className="s">same</span>
                  ) : type === 'material' ? (
                    <span className="ov">
                      <MaterialPick lib={lib} value={String(st.value ?? '')} filter={def.material_filter} onPick={save} />
                    </span>
                  ) : type === 'catalog_item' ? (
                    <span className="ov">
                      <CatalogPick lib={lib} value={String(st.value ?? '')} onPick={save} />
                    </span>
                  ) : type === 'recipe' ? (
                    <span className="ov">
                      <RecipePick lib={lib} value={String(st.value ?? '')} onPick={save} />
                    </span>
                  ) : (
                    <span className="ov">
                      <ValueEditor type={type} unit={unit} choices={def.choices?.map((c) => c.value) ?? null} value={st.value} disabled={!canEdit} onSave={save} />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="only">Set in Job setup; tick one to hold a value on this item. A held value is a change from standard, approved on the walk.</p>
    </div>
  );
}

/** The item's route as ZeroGap has it: each step with its station, the unit, its minutes locked; Adjust this item's route is the one unlock. */
function RouteRows({ ctx, item, res, row, canEdit, litKey, adjusting, onAdjust, patch }: { ctx: JobCtx; item: ResolvedItem; res: Resolution; row: Row | undefined; canEdit: boolean; litKey: string | null; adjusting: boolean; onAdjust: () => void; patch: (x: Row) => Promise<void> }) {
  const lib = ctx.r.lib!;
  const { steps, group } = itemShare(res, item);
  const shared = (group?.item_ids.length ?? 0) > 1;
  const ownLines = (row?.custom_route as Row[]) ?? [];
  const own = ownLines.map((l, k) => ({ l, k })).filter(({ l }) => l.removed);
  const editing = adjusting && canEdit && !item.parent_item_id;
  const [op, setOp] = useState('');
  // a quantity typed on a recipe step lands as the item's own line naming that step ({step_id, operation_id, qty}: own 'changed');
  // on a line already the item's own, the quantity on that line; the catalog is never written
  const typeQty = (st: (typeof steps)[number], v: string) => {
    if (st.own_index !== undefined && ownLines[st.own_index]) return patch({ custom_route: ownLines.map((l, k) => (k === st.own_index ? { ...l, qty: v } : l)) });
    return patch({ custom_route: [...ownLines, { step_id: st.step_id, operation_id: st.operation_id, qty: v }] });
  };
  const removeHere = (st: (typeof steps)[number]) =>
    window.confirm(`Leave "${st.operation_name}" out of this item's route? The recipe keeps it; restore brings it back.`) &&
    void patch({ custom_route: [...ownLines.filter((_, k) => k !== st.own_index), { step_id: st.step_id, operation_id: st.operation_id, qty: null, removed: true }] });
  return (
    <div className="sect">
      {steps.length === 0 ? (
        <p className="only">No route steps apply yet. Fill in the inputs.</p>
      ) : (
        <table className="lt route">
          <colgroup>
            <col />
            <col style={{ width: '22%' }} />
            <col style={{ width: 52 }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: 70 }} />
          </colgroup>
          <thead>
            <tr>
              <th>Step</th>
              <th>Station</th>
              <th className="r">Qty</th>
              <th>Unit</th>
              <th className="r">Minutes</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((st) => (
              <tr key={st.key} className={litKey === st.key ? 'lit' : st.own ? 'own' : ''}>
                <td>
                  {st.operation_name}
                  {st.own && (
                    <span className="tag chg" title={st.own === 'added' ? 'A step added on this job item beside its recipe; the catalog is untouched.' : 'A recipe step changed on this job item only; the catalog keeps its formula.'}>
                      {st.own === 'added' ? 'added on this item' : 'changed on this item'}
                    </span>
                  )}
                  {!st.verified && Number(st.shares[item.id]?.minutes ?? 0) > 0 && <UnverifiedTag items={[item]} to={verifyAddress(item, st.step_id)} title={`operation: ${st.operation_name}`} />}
                  <Chain
                    left
                    expr={st.qty_expr}
                    trace={st.trace}
                    note={
                      <>
                        {st.note && <div>{st.note}</div>}
                        <div>
                          {qty(st.qty)} {unitWords(st.unit)} × {qty(st.minutes_per_unit)} min + {qty(st.setup_minutes)} setup{st.setup_batches > 1 ? ` (${st.setup_batches} batches)` : ''}
                          {shared ? ' across this kind of item' : ''}, {st.labor_class_id ? lib.laborClasses[st.labor_class_id]?.name : 'no class'}.
                          {st.pool_qty !== st.qty && ` The job's total is ${qty(st.pool_qty)}, rounded up to whole units once; this is its share.`}
                        </div>
                      </>
                    }
                  />
                  {st.warnings.map((w) => (
                    <div key={w} className="warn">
                      {w}
                    </div>
                  ))}
                </td>
                <td className="s">{st.work_center_id ? lib.workCenters[st.work_center_id]?.name ?? '—' : '—'}</td>
                <td className="r">
                  {editing ? (
                    <Edit className={`short right${st.own !== undefined ? ' typed' : ''}`} value={qty(st.shares[item.id]?.qty ?? st.qty)} parse={numberOrNothing} onSave={(v) => typeQty(st, v)} />
                  ) : (
                    qty(st.shares[item.id]?.qty ?? st.qty)
                  )}
                </td>
                <td className="s">{unitWords(st.unit)}</td>
                <td className="r">
                  <span className="lk">{Math.round(Number(st.shares[item.id]?.minutes ?? 0) * 10) / 10}</span>
                  {editing && st.own === undefined && st.step_id && (
                    <button type="button" className="x" title="Leave this step out of this item's route (the recipe keeps it)" onClick={() => removeHere(st)}>
                      ×
                    </button>
                  )}
                  {canEdit && row && st.own !== undefined && (
                    <button
                      type="button"
                      className="x"
                      title={st.own === 'added' ? 'Remove this added step from this item' : 'Drop the change: the step prices as the recipe says again'}
                      onClick={() =>
                        window.confirm(st.own === 'added' ? `Remove "${st.operation_name}" from this item?` : `Drop this item's change to "${st.operation_name}"? It goes back to the recipe's formula.`) &&
                        void patch({ custom_route: ((row.custom_route as Row[]) ?? []).filter((_, k) => k !== st.own_index) })
                      }
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {own.length > 0 && (
        <p className="only">
          Removed on this item (the catalog keeps them):{' '}
          {own.map(({ l, k }) => (
            <span key={k}>
              {lib.operations[String(l.operation_id)]?.name ?? 'a step'}
              {canEdit && row && (
                <>
                  {' '}
                  <button type="button" className="linklike" onClick={() => void patch({ custom_route: ((row.custom_route as Row[]) ?? []).filter((_, i) => i !== k) })}>
                    restore
                  </button>
                </>
              )}{' '}
            </span>
          ))}
        </p>
      )}
      {editing && (
        <div className="row mid" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <OperationPick lib={lib} value={op} onPick={setOp} />
          <button
            type="button"
            className="btn"
            disabled={!op}
            onClick={async () => {
              await patch({ custom_route: [...ownLines, { operation_id: op, qty: 1 }] });
              setOp('');
            }}
          >
            Add step on this item
          </button>
        </div>
      )}
      {canEdit && !item.parent_item_id && (
        <div className="row mid" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <button type="button" className="btn" title={adjusting ? 'Close the editing boxes. Nothing is written by opening or closing them.' : 'Open the route and BOM for editing on this item only: type a quantity over a step or line, leave one out, or add one. Nothing is written until a value is saved; the recipe itself is edited in the Library.'} onClick={onAdjust}>
            {adjusting ? 'Done adjusting' : "Adjust this item's route"}
          </button>
          {item.catalog_item_id && <span className="only">{adjusting ? 'A quantity typed here holds on this item only; the catalog keeps its formula.' : 'The recipe itself is edited in the Library.'}</span>}
        </div>
      )}
    </div>
  );
}

/** A quantity box: a number, or nothing saved. */
const numberOrNothing = (t: string): string | undefined => (t.trim() === '' || !Number.isFinite(Number(t)) ? undefined : String(Number(t)));

/** The item's BOM: each line's quantity, its chain behind the i dot. */
function BomRows({ ctx, item, res, row, canEdit, litKey, adjusting, onAdjust, patch }: { ctx: JobCtx; item: ResolvedItem; res: Resolution; row: Row | undefined; canEdit: boolean; litKey: string | null; adjusting: boolean; onAdjust: () => void; patch: (x: Row) => Promise<void> }) {
  const lib = ctx.r.lib!;
  const { bom, group } = itemShare(res, item);
  const shared = (group?.item_ids.length ?? 0) > 1;
  const ownLines = (row?.custom_bom as Row[]) ?? [];
  const removed = ownLines.map((l, k) => ({ l, k })).filter(({ l }) => l.removed);
  const pricing = useMemo(() => lib.pricing, [lib]);
  const editing = adjusting && canEdit && !item.parent_item_id;
  const [mat, setMat] = useState('');
  // a quantity typed on a recipe line lands as the item's own line naming it ({line_id, material_id, qty}: own 'changed'); a line
  // already the item's own (added, changed, or carrying its own pricing) takes the quantity on that line
  const typeQty = (b: (typeof bom)[number], v: string) => {
    if (b.own_index !== undefined && ownLines[b.own_index]) return patch({ custom_bom: ownLines.map((l, k) => (k === b.own_index ? { ...l, qty: v } : l)) });
    return patch({ custom_bom: [...ownLines, { line_id: b.line_id, material_id: b.material_id, qty: v }] });
  };
  const removeHere = (b: (typeof bom)[number]) =>
    window.confirm(`Leave "${b.description}" out of this item's BOM? The recipe keeps it; restore brings it back.`) &&
    void patch({ custom_bom: [...ownLines.filter((_, k) => k !== b.own_index), { line_id: b.line_id, material_id: b.material_id, qty: null, removed: true }] });
  return (
    <div className="sect">
      {bom.length === 0 ? (
        <p className="only">No materials apply yet.</p>
      ) : (
        <table className="lt bom">
          <colgroup>
            <col />
            <col style={{ width: 110 }} />
          </colgroup>
          <thead>
            <tr>
              <th>Line</th>
              <th className="r">Qty</th>
            </tr>
          </thead>
          <tbody>
            {bom.map((b) => (
              <tr key={b.key} className={litKey === b.key ? 'lit' : b.own ? 'own' : ''}>
                <td>
                  {b.description}
                  {b.own && <span className="tag chg">{b.own === 'added' ? 'added on this item' : 'changed on this item'}</span>}
                  {b.sub_work && <span className="tag off">sub work</span>}
                  {!b.verified && b.material_id && <UnverifiedTag items={[item]} to={verifyAddress(item, b.line_id)} title={`material: ${b.description}`} />}
                  {b.typed_price && <span className="tag off">typed price</span>}
                  {b.separate_pricing && (
                    <span className={`tag ${b.pricing_own ? 'chg' : 'off'}`}>
                      separate pricing · {b.markup_pct} %{b.outside_margin ? ' · outside margin' : ''}
                    </span>
                  )}
                  {(b.links ?? [])
                    .filter((k) => k.item_id === item.id)
                    .map((k) => (
                      <a key={k.url} className="lnk sm" href={k.url} target="_blank" rel="noopener noreferrer" title={k.url}>
                        vendor page
                      </a>
                    ))}
                  <Chain
                    left
                    expr={b.qty_expr}
                    trace={b.trace}
                    note={
                      <>
                        {b.note && <div>{b.note}</div>}
                        <div>
                          {qty(b.qty)} {b.unit ?? ''} × ${b.price}
                          {shared ? ' for the group' : ''}
                          {b.material_role ? `, filled by "${b.material_role}"` : ''}
                          {b.typed_price ? ', the price typed on the item' : ''}
                          {b.pack ? `; ${bomPackWords(b.pack)}` : ''}
                          {b.tax_cents ? `; sales tax ${money(b.tax_cents)} on its cost` : ''}
                          {b.separate_pricing ? `; its own markup ${b.markup_pct} % (company ${pricing.materials_markup_pct} %)` : ''}.
                          {b.pool_qty !== b.qty ? ` The job buys ${qty(b.pool_qty)} ${b.unit ?? ''} in all, rounded up once; this is its share.` : ''}
                        </div>
                      </>
                    }
                  />
                  {b.warnings.map((w) => (
                    <div key={w} className="warn">
                      {w}
                    </div>
                  ))}
                </td>
                <td className="r">
                  {editing ? (
                    <>
                      <Edit className={`short right${b.own !== undefined ? ' typed' : ''}`} value={qty(b.shares[item.id]?.qty ?? b.qty)} parse={numberOrNothing} onSave={(v) => typeQty(b, v)} /> {b.unit ?? ''}
                    </>
                  ) : (
                    <>
                      {qty(b.shares[item.id]?.qty ?? b.qty)} {b.unit ?? ''}
                    </>
                  )}
                  {editing && b.own === undefined && b.line_id && (
                    <button type="button" className="x" title="Leave this line out of this item's BOM (the recipe keeps it)" onClick={() => removeHere(b)}>
                      ×
                    </button>
                  )}
                  {canEdit && row && b.own !== undefined && (
                    <button
                      type="button"
                      className="x"
                      title={b.own === 'added' ? 'Remove this added line from this item' : 'Drop the change: the line prices as the recipe says again'}
                      onClick={() =>
                        window.confirm(b.own === 'added' ? `Remove "${b.description}" from this item?` : `Drop this item's change to "${b.description}"? It goes back to the recipe's formula.`) &&
                        void patch({ custom_bom: ((row.custom_bom as Row[]) ?? []).filter((_, k) => k !== b.own_index) })
                      }
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {removed.length > 0 && (
        <p className="only">
          Removed on this item (the catalog keeps them):{' '}
          {removed.map(({ l, k }) => (
            <span key={k}>
              {l.material_id ? lib.materials[String(l.material_id)]?.description ?? 'a line' : String(l.label ?? 'a line')}
              {canEdit && row && (
                <>
                  {' '}
                  <button type="button" className="linklike" onClick={() => void patch({ custom_bom: ((row.custom_bom as Row[]) ?? []).filter((_, i) => i !== k) })}>
                    restore
                  </button>
                </>
              )}{' '}
            </span>
          ))}
        </p>
      )}
      {editing && (
        <div className="row mid" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <MaterialPick lib={lib} value={mat} onPick={setMat} />
          <button
            type="button"
            className="btn"
            disabled={!mat}
            onClick={async () => {
              await patch({ custom_bom: [...ownLines, { material_id: mat, qty: 1 }] });
              setMat('');
            }}
          >
            Add line on this item
          </button>
        </div>
      )}
      {canEdit && !item.parent_item_id && (
        <div className="row mid" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <button type="button" className="btn" title={adjusting ? 'Close the editing boxes. Nothing is written by opening or closing them.' : 'Open the route and BOM for editing on this item only. Nothing is written until a value is saved.'} onClick={onAdjust}>
            {adjusting ? 'Done adjusting' : "Adjust this item's BOM"}
          </button>
        </div>
      )}
    </div>
  );
}
